import { queryOptions, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost } from "../client";
import type {
  AcceptSellerTermsPayload,
  CreateStorePayload,
  SellEligibility,
  SellerStore,
  StoreSlugAvailability,
  UpdateStorePayload,
} from "../types";
import { qk } from "./qk";

// Opening a store (docs/plans/marketplace-commerce.md §8 Selling, M3) — served by the real server. Every response
// is the signed-in Person's own data, so these keys live under `account` and are removed on sign-in/out.

export const sellEligibilityQuery = () =>
  queryOptions({
    queryKey: qk.account.selling.eligibility(),
    queryFn: () => apiGet<SellEligibility>("/api/sell/eligibility"),
    staleTime: 0,
  });

export const myStoresQuery = () =>
  queryOptions({
    queryKey: qk.account.selling.stores(),
    queryFn: async () => (await apiGet<{ items: SellerStore[] }>("/api/me/stores")).items,
    staleTime: 0,
  });

export const storeSlugAvailabilityQuery = (slug: string) =>
  queryOptions({
    queryKey: qk.account.selling.slug(slug),
    queryFn: () => apiGet<StoreSlugAvailability>(`/api/sell/slugs/${encodeURIComponent(slug)}`),
    staleTime: 15_000,
    enabled: slug.length > 0,
  });

/** The store a Person is still setting up, if any (the server allows one draft at a time). */
export function draftStore(stores: readonly SellerStore[] | undefined): SellerStore | undefined {
  return stores?.find((store) => store.status === "draft" && store.role === "owner");
}

/** Replaces (or adds) one store in the cached list, keeping the list's order. */
function upsertStore(queryClient: QueryClient, store: SellerStore, previousSlug = store.slug) {
  queryClient.setQueryData<SellerStore[]>(qk.account.selling.stores(), (stores) => {
    if (!stores) return [store];
    const index = stores.findIndex((item) => item.id === store.id || item.slug === previousSlug);
    if (index === -1) return [store, ...stores];
    return stores.map((item, position) => (position === index ? store : item));
  });
}

export function useAcceptSellerTerms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AcceptSellerTermsPayload) =>
      apiPost<{ acceptance: NonNullable<SellEligibility["acceptance"]> }>("/api/sell/accept", payload),
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.account.selling.eligibility() }),
  });
}

export function useCreateStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStorePayload) => apiPost<{ store: SellerStore }>("/api/stores", payload),
    onSuccess: ({ store }) => {
      upsertStore(queryClient, store);
      // Slug checks cached before the store existed would now read "available" for someone else.
      queryClient.removeQueries({ queryKey: [...qk.account.selling.all(), "slug"] });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.account.selling.stores() });
      void queryClient.invalidateQueries({ queryKey: qk.account.selling.eligibility() });
    },
  });
}

export interface UpdateStoreVariables {
  slug: string;
  changes: UpdateStorePayload;
}

export function useUpdateStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, changes }: UpdateStoreVariables) =>
      apiPatch<{ store: SellerStore }>(`/api/stores/${encodeURIComponent(slug)}`, changes),
    onSuccess: ({ store }, { slug }) => {
      upsertStore(queryClient, store, slug);
      queryClient.removeQueries({ queryKey: [...qk.account.selling.all(), "slug"] });
      // A live store's public page and list rows show these fields.
      if (store.status === "active") void queryClient.invalidateQueries({ queryKey: qk.stores.all() });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.account.selling.stores() }),
  });
}

export function usePublishStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => apiPost<{ store: SellerStore }>(`/api/stores/${encodeURIComponent(slug)}/publish`, {}),
    onSuccess: ({ store }) => {
      upsertStore(queryClient, store);
      // The store is public now: every cached store list (and a cached 404 for its page) is out of date.
      void queryClient.resetQueries({ queryKey: qk.stores.all() });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.account.selling.stores() });
      void queryClient.invalidateQueries({ queryKey: qk.account.selling.eligibility() });
    },
  });
}
